// src/pages/RecursosHumanos/Colaboradores/colaboradoresData.ts
import { useEffect, useRef, useState } from "react";

/** ---------------------- Tipos da API ---------------------- */
export type ColaboradorAPI = {
  id?: string | number;
  nome_completo: string;
  cpf?: string | null;
  setor?: string | null; // exibido como "Departamento"
  data_de_nascimento?: string | null; // ISO
  status_atual?: string | null; // status atual do colaborador
};

type RespAPI = {
  ok: boolean;
  meta: any;
  data: ColaboradorAPI[];
};

/** ---------------------- Constantes ---------------------- */
const URL = `https://api.eva-googlemarine.com/departamentos/recursos-humanos/colaboradores-completo`;

// Endpoint opcional para await/long-poll (ajuste conforme seu backend)
const URL_AWAIT = `https://api.eva-googlemarine.com/departamentos/recursos-humanos/colaboradores/await`;

const POLL_FALLBACK_MS = 15000;
const ERROR_BACKOFF_MS = 5000;
const AWAIT_TIMEOUT_MS = 3000;
const AWAIT_GAP_MS = 120;

/** ---------------------- Utils ---------------------- */
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const jitter = (ms: number) => ms + Math.floor(Math.random() * 400);

/** Normaliza chaves que possam variar na API */
function normalizeColaboradores(arr: any[]): ColaboradorAPI[] {
  return (arr || []).map((x) => ({
    id: x.id ?? x.colaborador_id ?? x.user_id ?? undefined,
    nome_completo:
      x.nome_completo ?? x.nomeCompleto ?? x.nome ?? x.full_name ?? "",
    cpf: x.cpf ?? x.CPF ?? x.documento ?? null,
    setor: x.setor ?? x.departamento ?? x.area ?? null,
    data_de_nascimento:
      x.data_de_nascimento ?? x.nascimento ?? x.birth_date ?? null,
    status_atual:
      x.status_atual ?? x.statusAtual ?? x.status ?? x.situacao ?? null,
  }));
}

/** ---------------------- Fetchers ---------------------- */
export async function fetchColaboradoresOnce(): Promise<{
  data: ColaboradorAPI[];
  meta: RespAPI["meta"] | null;
}> {
  const res = await fetch(URL, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = (await res.json()) as RespAPI;

  if (!json?.ok) return { data: [], meta: json?.meta ?? null };

  const data = Array.isArray(json.data)
    ? normalizeColaboradores(json.data)
    : [];
  return { data, meta: json.meta ?? null };
}

type AwaitResult = { changed: boolean; versao: number | null };

async function awaitChange(
  since: number,
  signal?: AbortSignal
): Promise<AwaitResult | null> {
  try {
    const url = `${URL_AWAIT}?since=${encodeURIComponent(
      since
    )}&timeout_ms=${AWAIT_TIMEOUT_MS}`;
    const res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      signal,
    });
    if (!res.ok) return null;
    const json = await res.json();
    const versao =
      typeof json?.versao === "number" ? (json.versao as number) : since;
    const changed = Boolean(json?.changed);
    return { changed, versao };
  } catch {
    return null;
  }
}

/** ---------------------- Hook ---------------------- */
// Mantemos o nome `useEmpresasCliente` para não quebrar imports existentes.
// Agora retorna SOMENTE colaboradores de Técnico e Diretoria.
export function useEmpresasCliente() {
  const [data, setData] = useState<ColaboradorAPI[]>([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<RespAPI["meta"] | null>(null);

  const stop = useRef(false);
  const fetching = useRef(false);

  const versaoRef = useRef<number | null>(null);
  const awaitCtrlRef = useRef<AbortController | null>(null);
  const awaitSupported = useRef<boolean | null>(null);
  const awaitInFlightRef = useRef(false);

  // helper para filtrar apenas Técnico / Diretoria
  function filtrarDepartamentos(arr: ColaboradorAPI[]): ColaboradorAPI[] {
    return (arr || []).filter((c) => {
      const s = (c.setor || "").trim().toLowerCase();
      return s === "técnico" || s === "tecnico" || s === "diretoria";
    });
  }

  async function carregar() {
    fetching.current = true;
    try {
      const r = await fetchColaboradoresOnce();
      const filtrados = filtrarDepartamentos(r.data);
      setData(filtrados);
      setMeta(r.meta);
    } finally {
      fetching.current = false;
    }
  }

  useEffect(() => {
    stop.current = false;
    versaoRef.current = null;
    awaitSupported.current = null;
    awaitCtrlRef.current?.abort();

    (async () => {
      try {
        // 1) primeira carga
        await carregar();
        setLoading(false);

        // 2) capability detection do /await
        const init = await awaitChange(-1);
        if (init && typeof init.versao === "number") {
          versaoRef.current = init.versao;
          awaitSupported.current = true;
        } else {
          awaitSupported.current = false;
        }

        // 3) loop principal
        while (!stop.current) {
          while (fetching.current && !stop.current) await sleep(25);
          if (stop.current) break;

          const cur = versaoRef.current;

          if (awaitSupported.current !== false && cur != null) {
            if (awaitInFlightRef.current) {
              await sleep(50);
              continue;
            }

            const ac = new AbortController();
            awaitCtrlRef.current = ac;
            awaitInFlightRef.current = true;

            let resp: AwaitResult | null = null;
            try {
              resp = await awaitChange(cur, ac.signal);
            } catch (e: any) {
              if (e?.name !== "AbortError")
                await sleep(jitter(ERROR_BACKOFF_MS));
            } finally {
              if (awaitCtrlRef.current === ac) awaitCtrlRef.current = null;
              awaitInFlightRef.current = false;
            }

            if (stop.current) break;

            if (resp === null) {
              if (awaitSupported.current === null)
                awaitSupported.current = false;
              await sleep(POLL_FALLBACK_MS);
              continue;
            } else if (awaitSupported.current === null) {
              awaitSupported.current = true;
            }

            const avancou = (resp.versao ?? -1) > (cur ?? -1);
            if (resp.changed || avancou) {
              versaoRef.current = resp.versao ?? cur;
              await carregar();
              await sleep(20);
            } else {
              await sleep(AWAIT_GAP_MS);
            }
            continue;
          }

          // Fallback polling
          await sleep(POLL_FALLBACK_MS);
          await carregar();
        }
      } catch (e) {
        if (!stop.current) console.error("useEmpresasCliente loop error:", e);
        setLoading(false);
      }
    })();

    return () => {
      stop.current = true;
      awaitCtrlRef.current?.abort();
    };
  }, []);

  const refresh = async () => {
    while (fetching.current) await sleep(25);
    await carregar();
  };

  return { data, loading, refresh, meta };
}
